import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { RunContextItem } from '../../../store/customWorkflow/customWorkflows'
import { useGetExternalUserByIdQuery } from '../../../store/externalUser/externalUser'
import { useGetCrossClientQueryExecutionQuery } from '../../../store/crossClient/crossClient'
import { useGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { useLazyGetAiAgentQuery } from '../../../store/ai/aiAgentModel'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { meetingPrepPath, pastMeetingPath } from '../../../views/Scheduling/shared/bookingNavigation'
import { useGetRoomSessionQuery } from '../../../store/media/media'
import { isLiveSession } from '../../../common/session.util'
import { contextKindLabel } from './contextLabels'
import { parseMetadata } from '../../../common/util'

export interface RunContextItemRendererProps {
  item: RunContextItem
  accountId: string
}

const ContextLabel: React.FC<RunContextItemRendererProps> = ({ item }) => <span className='paragraph-small zocks-text-gray-600'>{contextKindLabel(item)}</span>

const ContactLink: React.FC<RunContextItemRendererProps> = ({ item, accountId }) => {
  const { data } = useGetExternalUserByIdQuery({ accountId, userId: item.contactId ?? '' }, { skip: !item.contactId })
  const name = getUserDisplayName(data?.data)
  if (!item.contactId || !name) return <ContextLabel item={item} accountId={accountId} />
  return (
    <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={`/library/manage-contacts/contacts/${item.contactId}`}>
      {name}
    </Link>
  )
}

const CcqClientLink: React.FC<{ clientId: string; accountId: string }> = ({ clientId, accountId }) => {
  const { data } = useGetExternalUserByIdQuery({ accountId, userId: clientId }, { skip: !clientId })
  return (
    <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={`/library/manage-contacts/contacts/${clientId}`}>
      {getUserDisplayName(data?.data) || clientId}
    </Link>
  )
}

const CcqExecutionLink: React.FC<RunContextItemRendererProps> = ({ item, accountId }) => {
  const { data } = useGetCrossClientQueryExecutionQuery({ accountId, executionId: item.executionId ?? '' }, { skip: !item.executionId })
  const question = data?.userQuestion
  const clientIds = item.clientIds ?? []

  if ((!item.executionId || !question) && clientIds.length === 0) return <ContextLabel item={item} accountId={accountId} />

  return (
    <span className='d-flex flex-column zocks-gap-1'>
      {item.executionId && question ? (
        <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={`/cross-client/executions/${item.executionId}`}>
          {`Client Query, question: ${question}`}
        </Link>
      ) : (
        <ContextLabel item={item} accountId={accountId} />
      )}
      {clientIds.length > 0 && (
        <span className='d-flex flex-wrap zocks-gap-1'>
          {clientIds.map((clientId, index) => (
            <React.Fragment key={clientId}>
              {index > 0 && <span>{', '}</span>}
              <CcqClientLink clientId={clientId} accountId={accountId} />
            </React.Fragment>
          ))}
        </span>
      )}
    </span>
  )
}

type AgentTypeStrategy = React.FC<RunContextItemRendererProps>

const createMeetingLinkStrategy = (pathFn: (roomId: string, sessionId: string) => string): AgentTypeStrategy => {
  const MeetingLinkStrategy: AgentTypeStrategy = ({ item, accountId }) => {
    const { data: result } = useGetAiAgentResultQuery(
      { accountId, type: item.agentType ?? '', resultKey: item.aiAgentResultKey ?? '' },
      { skip: !item.agentType || !item.aiAgentResultKey },
    )
    const [triggerGetAiAgent, { data: agent }] = useLazyGetAiAgentQuery()
    const originalAiAgentId = result?.originalAiAgentId

    useEffect(() => {
      if (originalAiAgentId) {
        void triggerGetAiAgent({ accountId, aiAgentId: originalAiAgentId })
      }
    }, [accountId, originalAiAgentId, triggerGetAiAgent])

    const context = agent?.context
    const roomId = context?.roomId ?? context?.room_id
    const sessionId = context?.sessionId ?? context?.session_id

    if (!roomId || !sessionId) return <ContextLabel item={item} accountId={accountId} />
    return (
      <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={pathFn(roomId, sessionId)}>
        {contextKindLabel(item)}
      </Link>
    )
  }
  return MeetingLinkStrategy
}

const MeetingSummaryStrategy = createMeetingLinkStrategy(pastMeetingPath)
const MeetingPreparationStrategy = createMeetingLinkStrategy(meetingPrepPath)

const AGENT_TYPE_STRATEGIES: Record<string, AgentTypeStrategy> = {
  meetingSummary: MeetingSummaryStrategy,
  meetingPreparation: MeetingPreparationStrategy,
}

const AiAgentResultLink: React.FC<RunContextItemRendererProps> = ({ item, accountId }) => {
  const Strategy = item.agentType ? AGENT_TYPE_STRATEGIES[item.agentType] : undefined
  if (!Strategy) return <ContextLabel item={item} accountId={accountId} />
  return <Strategy item={item} accountId={accountId} />
}

const MeetingLink: React.FC<RunContextItemRendererProps> = ({ item, accountId }) => {
  const roomId = item.roomId ?? ''
  const sessionId = item.sessionId ?? ''
  const { data, isFetching } = useGetRoomSessionQuery({ accountId, roomId, sessionId }, { skip: !roomId || !sessionId })

  if (!roomId || !sessionId || isFetching || !data?.data) return <ContextLabel item={item} accountId={accountId} />

  const href = isLiveSession(data.data) ? meetingPrepPath(roomId, sessionId) : pastMeetingPath(roomId, sessionId)
  const meetingTitle = parseMetadata(data.data.metadata)?.external?.title
  return (
    <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={href}>
      {meetingTitle || contextKindLabel(item)}
    </Link>
  )
}

const RENDERERS: Record<RunContextItem['kind'], React.FC<RunContextItemRendererProps>> = {
  contact: ContactLink,
  ccqExecution: CcqExecutionLink,
  aiAgentResult: AiAgentResultLink,
  event: ContextLabel,
  meeting: MeetingLink,
}

export const RunContextItemLink: React.FC<RunContextItemRendererProps> = ({ item, accountId }) => {
  const Renderer = RENDERERS[item.kind]
  return <Renderer item={item} accountId={accountId} />
}
